/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { ReactElement } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import rehypeAttr from 'rehype-attr';
import { filter } from 'unist-util-filter';
import InformationCircleIcon from '../../../icons/information-circle';
import ListCheckIcon from 'components/icons/list-check';
import Overlay from 'components/revelo-ui/content/overlay';

export interface Benefit {
  title: string;
  icon: string;
  attributeLink: {
    link: string;
    openInNewWindow: boolean;
    type: string;
  };
  attributeLinkText: string;
  tooltipContent: string;
}

export interface Props {
  readonly benefits: object;
  readonly classNames?: string;
  readonly showToolTips?: boolean;
}

const DynamicBenefitsList = ({ benefits, classNames, showToolTips }: Props) => {
  function getBenefitIcon(benefit: Benefit): ReactElement {
    if (benefit.icon.length > 0) {
      const IconComponent = dynamic<{ className: string; 'aria-hidden': string }>(
        () => import(`components/icons/${benefit.icon}`),
      );

      return <IconComponent className="my-auto size-6 shrink-0" aria-hidden="true" />;
    } else {
      return <ListCheckIcon className="size-6 shrink-0" aria-hidden="true" />;
    }
  }

  const triggerIcon: React.JSX.Element = <InformationCircleIcon className="relative size-6 text-neutral-800 md:ml-1" />;

  const removeComments = () => {
    const filters = ['comment', 'raw'];

    return (tree) => filter(tree, { cascade: false }, (node) => !filters.includes(node.type));
  };

  const getOverlayContent = (benefit: Benefit): ReactElement => (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <ReactMarkdown
          className="markdown default prose"
          rehypePlugins={[[rehypeAttr, { properties: 'attr' }], removeComments]}
        >
          {benefit.tooltipContent}
        </ReactMarkdown>
      </div>
      <div className="mb-4 flex gap-2">
        <Link
          href={benefit.attributeLink.link}
          target={benefit.attributeLink.openInNewWindow ? '_blank' : '_self'}
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700"
        >
          {benefit.attributeLinkText}
        </Link>
      </div>
    </div>
  );

  return (
    <div className={classNames}>
      {benefits &&
        Object.values(benefits).map((benefit, i) => (
          <div className="flex w-full gap-2 text-sm" key={i}>
            {getBenefitIcon(benefit)}
            <span className="inline-flex w-[300px] items-center text-left md:w-full">
              <span className="relative ">{benefit.title}</span>
              {showToolTips && benefit.tooltipContent && (
                <Overlay
                  idSelector={`benefit-modal-${i}`}
                  wrapperClassNames="text-left contents"
                  text={getOverlayContent(benefit)}
                  triggerType="buttonClick"
                  triggerText={triggerIcon}
                  maxHeight="80%"
                  textAlign="left"
                />
              )}
            </span>
          </div>
        ))}
    </div>
  );
};

export default DynamicBenefitsList;
